import _ from 'lodash';
import React, { useState } from 'react';
import { arrayOf, bool, func, string } from 'prop-types';
import { reportToSegment, types, eventNames } from '@smartcar/morse';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Button, Tooltip, Typography, useTheme } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';

import { RedirectUri, RedirectUriForm } from './components';
import PermissionGate from '../../../../../../components/PermissionGate';
import staticText from '../../../../../../localization/Application/Configuration/configuration';
import { URI_LIMIT } from './components/RedirectUriForm/RedirectUriForm';

const RedirectUriList = ({
  redirectUris,
  updateUris,
  clientId,
  isUpdating,
  defaultRedirectUri,
  setDefaultRedirectUri,
  isFetchingDefaultRedirectUri,
}) => {
  const { webhooksV2Enabled, vehicleAccessTabEnabled } = useFlags();
  const theme = useTheme();
  const [showRedirectUriForm, setShowRedirectUriForm] = useState(false);

  const redirectUriLimitReached = redirectUris.length >= URI_LIMIT;

  const handleCreate = (redirectUri) => {
    const updatedUris = _.concat(redirectUris, redirectUri);
    updateUris(updatedUris);
  };

  const handleRemove = (redirectUri) => {
    const updatedUris = _.filter(redirectUris, value =>
      redirectUri !== value,
    );
    updateUris(updatedUris);
    reportToSegment(types.TRACK, eventNames.buttonClicked, {
      label: 'backend action',
      text: staticText.oauth.redirectUris.delete,
    });
  };

  const buildRedirectUris = _.map(redirectUris, (redirectUri, index) => (
    <RedirectUri
      key={index}
      index={index}
      redirectUri={redirectUri}
      removeRedirectUri={handleRemove}
      // temporarily gate next two props until vehicleAccessTab is fully launched
      isDefault={
        (webhooksV2Enabled && vehicleAccessTabEnabled) &&
        !isFetchingDefaultRedirectUri &&
        (defaultRedirectUri === redirectUri || (!defaultRedirectUri && index === 0))
      }
      setDefaultRedirectUri={(webhooksV2Enabled && vehicleAccessTabEnabled)
        && setDefaultRedirectUri}
    />
  ));

  const addButton = (
    <Button
      variant="outlined"
      color="secondary"
      size="small"
      startIcon={<AddRoundedIcon fontSize="small" />}
      onClick={() => setShowRedirectUriForm(true)}
      disabled={redirectUris.length >= URI_LIMIT}
    >
      {staticText.oauth.redirectUris.add}
    </Button>
  );

  const closeForm = () => {
    setShowRedirectUriForm(false);
  };

  return (
    <div>
      <Typography variant="body2" sx={{ fontFamily: theme.typography.bold.fontFamily, marginBottom: 1 }}>
        {staticText.oauth.redirectUris.heading}
      </Typography>
      <div>
        {redirectUris.length > 0
          ? buildRedirectUris
          : (
            <Typography variant="body2" component="p" mb={1} maxWidth={theme.width.paragraph}>
              {staticText.oauth.redirectUris.emptyState.text}
            </Typography>
          )
        }
        {showRedirectUriForm && !redirectUriLimitReached ? (
          <RedirectUriForm
            createRedirectUri={handleCreate}
            clientId={clientId}
            isUpdating={isUpdating}
            redirectUris={redirectUris}
            closeForm={closeForm}
          />
        ) : (
          <PermissionGate dashboardPermission="write_configuration">
            {!redirectUriLimitReached ? addButton : (
              <Tooltip title={staticText.oauth.redirectUris.limitMessage} arrow placement="right">
                <span>{addButton}</span>
              </Tooltip>
            )}
          </PermissionGate>
        )}
      </div>
    </div>
  );
};

export default RedirectUriList;

RedirectUriList.propTypes = {
  updateUris: func.isRequired,
  redirectUris: arrayOf(string).isRequired,
  clientId: string.isRequired,
  isUpdating: bool.isRequired,
  defaultRedirectUri: string,
  setDefaultRedirectUri: func.isRequired,
  isFetchingDefaultRedirectUri: bool.isRequired,
};

RedirectUriList.defaultProps = {
  defaultRedirectUri: null,
};
